/**
 * Where the dashboard should call pear-payments (Zana, checkout, addons, …).
 *
 * Local dev: CRA proxies `/api` and `/health` to :8080 (see setupProxy.js), so we
 * use same-origin URLs and avoid CORS / mixed health probes when
 * REACT_APP_PAYMENTS_URL is http://localhost:8080 or unset.
 */

function isLocalDevDashboard(): boolean {
  return (
    process.env.NODE_ENV === 'development'
    && typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  );
}

/** Hosted dashboard default — matches render.yaml and paymentsApi checkout fallback. */
export const DEFAULT_PAYMENTS_URL = 'https://pear-payments.onrender.com';

function isRenderHostedDashboard(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h.endsWith('.onrender.com') || h === 'trasseapp.com' || h === 'www.trasseapp.com';
}

/** Owner-facing hint when Zana cannot reach the payments API. */
export function zanaServerSetupHint(): string {
  if (isRenderHostedDashboard()) {
    return 'On Render preview, Zana AI talks to https://pear-payments.onrender.com — no local terminal. Hard-refresh this tab after deploying the latest dashboard.';
  }
  return 'On localhost: `cd pear-payments-backend && npm run dev` (port 8080), then restart `npm start` in trasseapp-dashboard.';
}

function envPaymentsUrl(): string {
  let url = (process.env.REACT_APP_PAYMENTS_URL || '').trim().replace(/\/$/, '');
  // Preview/prod on Render must never call localhost:8080 baked in from a dev machine.
  if (isRenderHostedDashboard() && isLocalPaymentsEnv(url)) {
    url = DEFAULT_PAYMENTS_URL;
  }
  return url;
}

function isLocalPaymentsEnv(url: string): boolean {
  return !url || /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(url);
}

/** Base URL with no trailing slash; empty string = same origin (dev proxy). */
export function getPaymentsBaseUrl(): string {
  const fromEnv = envPaymentsUrl();
  if (process.env.REACT_APP_PAYMENTS_PROXY === '0') return fromEnv;
  if (isLocalDevDashboard() && isLocalPaymentsEnv(fromEnv)) return '';
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
    return DEFAULT_PAYMENTS_URL;
  }
  return fromEnv;
}

/** True when Zana / payments calls can be attempted (env URL or local proxy). */
export function isPaymentsApiConfigured(): boolean {
  if (getPaymentsBaseUrl()) return true;
  return isLocalDevDashboard() && isLocalPaymentsEnv(envPaymentsUrl());
}

export function paymentsApiUrl(path: string): string {
  const base = getPaymentsBaseUrl();
  const p = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${p}` : p;
}
